import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { ApiClientError, idempotencyKeyFor } from '@/lib/http-core';
import {
  EXPOSED_MALLS,
  type BankAccountDetail,
  type BankAccountPayload,
  type BankAccountSummary,
  type ExposedMall,
} from '../types/bankAccount';

/**
 * 무통장 입금 계좌 마스터 admin CRUD. (be-order 동결 계약 — order-service admin.)
 * 게이트웨이 `/admin/v1/orders/**` → BE `/api/admin/v1/orders/**` RewritePath 재사용
 * (orderPolicy / shippingFee / orderFormFields 와 동일). v2 envelope
 * `{ success, data, error, meta }`. write 는 Idempotency-Key 수용(lenient).
 * 경로가 generated OpenAPI 스펙에 미반영이라 타입 클라이언트 대신 adminFetch 로 호출한다.
 *
 * be-order 동결 계약: 목록 응답 = `data.accounts[]`(전체 필드), 단건/생성(201)/수정 =
 * BankAccountResponse, DELETE=soft-delete(data null). RBAC=SUPER_ADMIN/OPS_ADMIN.
 */
const BASE = '/admin/v1/orders/bank-accounts';

/**
 * 목록 envelope — be-order 동결 = `data.accounts[]`. 과거 잠정 키 `bankAccounts`
 * 도 허용해 계약 표기 흔들림에 안전(둘 중 채워진 쪽을 사용). null 이면 빈 배열.
 */
interface ListEnvelope {
  success: boolean;
  data?: {
    accounts?: BankAccountSummary[] | null;
    bankAccounts?: BankAccountSummary[] | null;
  } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: BankAccountDetail | null;
}

const VALID_MALLS = new Set<string>(EXPOSED_MALLS);

/**
 * 상세를 정규화한다 — BE 가 exposedMalls 를 null 로 줄 수도 있어 렌더 전에 배열 보장
 * (`.map` 크래시 방지). 미지원 몰 키는 제거(BO 탭은 4종만 노출). orderFormFields
 * 정책 hooks 의 동일 가드.
 */
const normalizeDetail = (p: BankAccountDetail): BankAccountDetail => ({
  ...p,
  exposedMalls: (p.exposedMalls ?? []).filter((m): m is ExposedMall =>
    VALID_MALLS.has(m),
  ),
});

interface ErrorEnvelopeShape {
  error?: { code?: string; message?: string } | null;
}

/**
 * write(POST/PUT) 실패 시 BE 에러 envelope(`{error:{code,message}}`)를 파싱해
 * `ApiClientError` 로 던진다. friendlyMessage 가 BE 메시지를 그대로 노출하므로
 * 검증 메시지(중복 계좌·필수값 누락 등)가 토스트에 정확히 표시된다. 본문 파싱
 * 실패(비-envelope)면 status 기반 일반 Error 로 폴백.
 */
const throwWriteError = async (res: Response, what: string): Promise<never> => {
  try {
    const body = (await res.json()) as ErrorEnvelopeShape;
    const err = body?.error;
    if (err?.code || err?.message) {
      throw new ApiClientError(
        { code: err.code ?? 'COMMON_BAD_REQUEST', message: err.message ?? '' },
        { status: res.status },
      );
    }
  } catch (e) {
    if (e instanceof ApiClientError) throw e;
    // JSON 파싱 실패 등은 무시하고 일반 Error 폴백.
  }
  throw new Error(`bank account ${what} failed: ${res.status}`);
};

export const bankAccountsQueryKey = () => ['admin', 'bank-accounts'] as const;

export const bankAccountQueryKey = (bankAccountId: string) =>
  ['admin', 'bank-account', bankAccountId] as const;

const fetchList = async (): Promise<BankAccountSummary[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`bank accounts list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  // be-order 동결 키 = accounts. 잠정 키 bankAccounts 도 폴백 허용.
  const list = env.data?.accounts ?? env.data?.bankAccounts ?? [];
  return list.map(normalizeDetail);
};

const fetchOne = async (bankAccountId: string): Promise<BankAccountDetail> => {
  const res = await adminFetch(`${BASE}/${bankAccountId}`);
  if (!res.ok) throw new Error(`bank account fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return normalizeDetail(env.data);
};

export const useBankAccountsQuery = () =>
  useQuery<BankAccountSummary[]>({
    queryKey: bankAccountsQueryKey(),
    queryFn: fetchList,
  });

export const useBankAccountQuery = (bankAccountId: string | null) =>
  useQuery<BankAccountDetail>({
    queryKey: bankAccountQueryKey(bankAccountId ?? ''),
    queryFn: () => fetchOne(bankAccountId as string),
    enabled: bankAccountId != null,
  });

export const useBankAccountMutation = () => {
  const qc = useQueryClient();
  const invalidateList = () =>
    qc.invalidateQueries({ queryKey: bankAccountsQueryKey() });

  const create = useMutation({
    mutationFn: async (payload: BankAccountPayload): Promise<BankAccountDetail> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwWriteError(res, 'create');
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return normalizeDetail(env.data);
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({
      bankAccountId,
      payload,
    }: {
      bankAccountId: string;
      payload: BankAccountPayload;
    }): Promise<BankAccountDetail> => {
      const path = `${BASE}/${bankAccountId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('PUT', path, payload),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwWriteError(res, 'update');
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return normalizeDetail(env.data);
    },
    onSuccess: (_, { bankAccountId }) => {
      void qc.invalidateQueries({ queryKey: bankAccountQueryKey(bankAccountId) });
      invalidateList();
    },
  });

  const remove = useMutation({
    mutationFn: async (bankAccountId: string): Promise<void> => {
      const path = `${BASE}/${bankAccountId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('DELETE', path, ''),
        },
      });
      if (!res.ok) throw new Error(`bank account delete failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  return { create, update, remove };
};
